import { Link } from "react-router-dom";
import { MarketingCta, MarketingLayout } from "@/components/marketing/MarketingLayout";

const FOUNDATION_GROUPS = [
  {
    title: "Website & storefront",
    items: [
      "Website, menu & product photos",
      "Custom domain",
      "Multi-language (DE/AT/IT/CH/PT)",
      "6 preset themes",
      "QR code generator",
      "Basic SEO (meta tags)",
      "Social + delivery partner links",
    ],
  },
  {
    title: "Orders & operations",
    items: [
      "CRM dashboard — edit menu & pricing",
      "Out-of-stock toggle",
      "Order actions — accept, reject, mark ready",
      "Book & Reserve — accept table, slot, or appointment requests",
      "Owner + 1 staff seat",
    ],
  },
  {
    title: "AI tools",
    items: [
      "Unlimited AI copy & SEO text",
      "1 free AI product image, then add-on",
    ],
  },
  {
    title: "Analytics & support",
    items: [
      "Basic analytics — orders, revenue, best-sellers, visits",
      "Standard support",
    ],
  },
];

export function MarketingPricingPage() {
  return (
    <MarketingLayout activePath="/pricing">
      <section className="pricing-hero" data-testid="public-pricing-content">
        <div className="wrap">
          <span className="eyebrow">Pricing</span>
          <h1 className="headline" data-testid="public-pricing-title">
            One flat plan. <em>No commission, ever.</em>
          </h1>
          <p className="sub" data-testid="public-pricing-body">
            Foundation covers everything a business needs to launch. Add only what you need beyond that, whenever you need it.
          </p>
        </div>
      </section>

      <section className="plan-section" data-testid="pricing-foundation">
        <div className="wrap">
          <div className="plan-card">
            <div className="plan-head">
              <div>
                <h3>Foundation</h3>
                <p>Everything a business needs to establish itself online and keep growing.</p>
              </div>
              <div className="plan-price">€99<span>/mo</span></div>
            </div>
            {FOUNDATION_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="feature-cat">{group.title}</p>
                <div className="feature-grid2">
                  {group.items.map((item) => (
                    <p key={item}><span className="check">✓</span>{item}</p>
                  ))}
                </div>
              </div>
            ))}
            <p className="plan-note">3-month minimum to start, then cancel anytime, month to month.</p>
          </div>
        </div>
      </section>

      <section className="addons-section" data-testid="pricing-addons">
        <div className="wrap">
          <div className="section-label">Growth — monthly, cancel anytime</div>
          <div className="addon-card" style={{ borderColor: "var(--terracotta)", borderWidth: 1.5 }}>
            <div className="addon-top">
              <div>
                <h4>
                  Growth Pack <span className="tag">Popular</span>{" "}
                  <span className="tag" style={{ background: "var(--cream-2)", color: "var(--forest-2)" }}>+ Priority support</span>
                </h4>
                <p className="addon-desc">Everything you need to actively market and grow one location.</p>
              </div>
              <div className="price">€35<span>/location/mo</span></div>
            </div>
            <div className="addon-list">
              <p>✓ WhatsApp order notifications</p>
              <p>✓ Google reviews auto-sync</p>
              <p>✓ Discount &amp; merchant campaigns</p>
              <p>✓ Highlight items — Chef&apos;s Pick, Fresh Today, Best Seller</p>
              <p>✓ Loyalty &amp; rewards program</p>
              <p>✓ Manager seat for this location</p>
              <p>✓ Advanced analytics</p>
            </div>
          </div>

          <div className="section-label">À la carte — add only what you need</div>
          <div className="simple-addon">
            <div>
              <h4>Extra staff / manager seat <span className="tag" style={{ fontSize: 10, background: "var(--cream-2)", color: "var(--forest-2)", padding: "2px 8px", borderRadius: 20 }}>+ Priority support</span></h4>
              <p>Shared across your whole account</p>
            </div>
            <div className="price">€12<span>/seat/mo</span></div>
          </div>
          <div className="simple-addon">
            <div>
              <h4>Additional location <span className="tag" style={{ fontSize: 10, background: "var(--cream-2)", color: "var(--forest-2)", padding: "2px 8px", borderRadius: 20 }}>+ Priority support</span></h4>
              <p>Includes Foundation for that location</p>
            </div>
            <div className="price">€40<span>/location/mo</span></div>
          </div>
          <div className="simple-addon">
            <div>
              <h4>Catering / pre-order module <span className="tag" style={{ fontSize: 10, background: "var(--cream-2)", color: "var(--forest-2)", padding: "2px 8px", borderRadius: 20 }}>+ Priority support</span></h4>
              <p>Per location - For the big stuff — bulk and made-to-order requests that need real lead time: catering trays, wedding cakes, event florals. Not for table bookings or quick reservations — those are included free with Book & Reserve in Foundation.</p>
            </div>
            <div className="price">€25<span>/mo</span></div>
          </div>

          <div className="section-label">One-time purchases — no priority support change</div>
          <div className="simple-addon">
            <div>
              <h4>Custom brand colors</h4>
              <p>Go beyond the 6 preset themes</p>
            </div>
            <div className="price">€15<span> one-time</span></div>
          </div>
          <div className="simple-addon">
            <div>
              <h4>Extra AI image credits</h4>
              <p>Top up anytime from your dashboard</p>
            </div>
            <div className="price">€10<span> / 20 images</span></div>
          </div>
        </div>
      </section>

      <section className="pricing-faq" data-testid="pricing-note">
        <div className="wrap">
          <p>Any active monthly add-on unlocks priority support for your whole account — live chat, faster response times, and a named contact.</p>
          <p>
            More questions? See the full{" "}
            <Link to="/#faq" style={{ color: "var(--terracotta)", fontWeight: 600 }}>FAQ</Link>.
          </p>
        </div>
      </section>

      <MarketingCta
        title="Start with Foundation today"
        body="Nothing's charged until you're ready to go live."
      />
    </MarketingLayout>
  );
}
